<script setup>
import { useForm } from '@inertiajs/vue3'
import { useMainStore } from '@/stores/main.js'
import { storeToRefs } from 'pinia'
import ForgotPasswordModal from './ForgotPasswordModal.vue'

const props = defineProps({
    errors: {
        type: Object,
        default: () => ({}),
    },
    flash: {
        type: Object,
        default: () => ({}),
    },
    isMenu: {
        type: Boolean,
        default: false,
    },
})

const store = useMainStore()
const { user, userName, isLoggedIn, isUserPage } = storeToRefs(store)


const form = useForm({
    email: '',
    password: '',
})

const hasError = (field) => Boolean(props.errors?.[field])
const errorText = (field) => {
    const val = props.errors?.[field]
    if (!val) return ''
    return Array.isArray(val) ? val[0] : val
}
const errorMessages = (field) => (hasError(field) ? [errorText(field)] : [])

const submit = () => {
  form.post(route('login'), {})
}
</script>

<template>
    <!-- Show user greeting if logged in -->
    <a
        v-if="isLoggedIn && !isUserPage"
        :href="route('web.user.dashboard')"
        class="relative inline-flex items-center gap-2 rounded-[10px] px-4 py-3 text-sm font-semibold text-iron-grey border-2 border-iron-grey"
    >
        <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
        <span class="hidden sm:inline">Pozdravljen {{ userName }}</span>
    </a>

    <!-- Show login modal if not logged in -->
    <v-dialog
        v-else-if="!isUserPage && !isLoggedIn"
        max-width="480"
        persistent
    >
        <template v-slot:activator="{ props: activatorProps }">
            <button
                v-bind="activatorProps"
                class="relative inline-flex items-center gap-2 rounded-[10px] px-6 py-4 text-sm font-semibold text-iron-grey border-2 border-iron-grey hover:bg-iron-grey hover:text-white"
                :class="{
                    'w-full rounded-none': isMenu,
                }"
                >
                <span>Prijava</span>
            </button>
            
        </template>

        <template v-slot:default="{ isActive }">
            <v-card
                class="overflow-hidden rounded-3xl shadow-xl shadow-iron-grey-light/20"
            >
                <div class="border-b border-iron-grey-light/30 bg-linen px-6 py-4">
                    <p class="text-xs font-semibold uppercase tracking-[0.2em] text-iron-grey">Prijava</p>
                    <p class="mt-1 text-base font-semibold text-iron-grey">Dobrodošli nazaj v Kulfood</p>
                    <p class="mt-1 text-xs text-iron-grey/70">Prijavite se za dostop do vašega računa in naročil.</p>
                </div>

                <v-card-text class="px-6 py-5 space-y-4">
                    <div v-if="flash.success" class="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" role="alert">
                        {{ flash.success }}
                    </div>
                    <div v-if="flash.error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                        {{ flash.error }}
                    </div>

                    <v-form @submit.prevent="submit()">
                        <div class="space-y-4">
                            <v-text-field
                                v-model="form.email"
                                label="Email"
                                type="email"
                                density="comfortable"
                                variant="outlined"
                                autocomplete="email"
                                required
                                :error="hasError('email')"
                                :error-messages="errorMessages('email')"
                            ></v-text-field>

                            <v-text-field
                                v-model="form.password"
                                label="Geslo"
                                type="password"
                                density="comfortable"
                                variant="outlined"
                                autocomplete="current-password"
                                required
                                :error="hasError('password')"
                                :error-messages="errorMessages('password')"
                            ></v-text-field>

                            

                            <div class="mt-2 flex justify-end gap-2">
                                <v-btn
                                    class="text-sm font-medium px-5 bg-white text-iron-grey border border-iron-grey-light hover:bg-linen rounded-xl"
                                    @click="isActive.value = false"
                                >
                                    Prekliči
                                </v-btn>

                                <v-btn
                                    class="text-sm font-semibold px-5 bg-white text-iron-grey border border-iron-grey-light hover:bg-linen rounded-xl"
                                    @click="submit()"
                                >
                                    Prijavi se
                                </v-btn>
                            </div>
                        </div>
                    </v-form>

                    <v-divider
                        :thickness="1"
                        class="border-opacity-100 my-4"
                        color="grey-lighten-2"
                    ></v-divider>

                    <div class="flex items-center justify-space-between">
                        <p class="text-xs text-iron-grey/70">
                            Še nimate računa?
                            <a :href="route('register')" class="font-semibold text-iron-grey hover:text-iron-grey/80">Registrirajte se</a>
                        </p>
                        <ForgotPasswordModal :errors="errors" :flash="flash" />
                    </div>
                    
                </v-card-text>
            </v-card>
        </template>
    </v-dialog>
</template>
